import React, { createContext, type FC, useEffect, useState } from 'react';
import { listAppTemplates } from '@wix/ambassador-devcenter-apps-v1-app-template/http';
import type { AppTemplate } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { useHttpClient } from '@wix/fe-essentials/react';
import { useTemplatesPageBi } from '../hooks/useTemplatesPageBi';
import { getLoggedInWorkspace } from '@wix/ambassador-os-v1-workspace/http';
import { WorkspaceType } from '@wix/ambassador-os-v1-workspace/types';
import { getIsLoggedInByCookie } from '../utils/getIsLoggedInByCookie';
import { useEssentials } from '@wix/fe-essentials-standalone';

export interface TemplatesContextProps {
  templates: AppTemplate[];
  isUserLoggedIn: boolean;
  templatesLoaded: boolean;
  workspace: WorkspaceType | undefined;
}

export const TemplatesContext = createContext<TemplatesContextProps>({
  templates: [],
  isUserLoggedIn: false,
  templatesLoaded: false,
  workspace: undefined,
});

export const TemplatesContextProvider: FC = ({ children }) => {
  const httpClient = useHttpClient();
  const isUserLoggedIn = getIsLoggedInByCookie();
  const { panoramaClient } = useEssentials();
  const { reportMainPageLoaded } = useTemplatesPageBi();
  const [templates, setTemplates] = useState<AppTemplate[]>([]);
  const [templatesLoaded, setTemplatesLoaded] = React.useState<boolean>(false);
  const [workspace, setWorkspace] = React.useState<WorkspaceType | undefined>(
    undefined,
  );

  const loadTemplates = async (): Promise<AppTemplate[]> => {
    const {
      data: { appTemplates },
    } = await httpClient.request(listAppTemplates({}));
    return appTemplates || [];
  };

  const loadWorkspace = async (): Promise<WorkspaceType | void> => {
    if (isUserLoggedIn) {
      try {
        const {
          data: {
            workspace: { type },
          },
        } = await httpClient.request(getLoggedInWorkspace({}));
        return type || WorkspaceType.UNKNOWN_TYPE;
      } catch {
        return WorkspaceType.UNKNOWN_TYPE;
      }
    }
  };

  useEffect(() => {
    panoramaClient.reportLoadStart();
    Promise.all([loadTemplates(), loadWorkspace()])
      .then(([appTemplates, loggedInWorkspace]) => {
        setTemplates(appTemplates);
        loggedInWorkspace && setWorkspace(loggedInWorkspace);
      })
      .catch(panoramaClient.errorMonitor().reportError)
      .finally(() => {
        panoramaClient.reportLoadFinish();
        reportMainPageLoaded();
        setTemplatesLoaded(true);
      });
  }, []);

  return (
    <TemplatesContext.Provider
      value={{
        templates,
        isUserLoggedIn,
        templatesLoaded,
        workspace,
      }}
    >
      {children}
    </TemplatesContext.Provider>
  );
};
